import React from 'react';
import { Dialog, IconButton, makeStyles } from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Close } from '@carbon/icons-react';
import { ModalType } from './NotFoundQuotationContainer';
import { useRegisterRequestRfqQuotationMutation } from '@generated/graphql';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from '@components/Provider';

interface Props {
  open: boolean;
  type: ModalType;
  rfqId?: string;
  partnerId?: string;
  onClose: () => void;
}
const RequestForIssueModal = ({
  open,
  type,
  rfqId,
  partnerId,
  onClose
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [trigger] = useRegisterRequestRfqQuotationMutation();

  const onClick = async () => {
    if (!rfqId || !partnerId) return;

    const { data } = await trigger({
      variables: {
        input: {
          rfqId,
          partnerId
        }
      }
    });

    const result = data?.registerRequestRfqQuotation.result;

    if (!data) {
      return enqueueSnackbar(
        '네트워크 문제로 서비스에 문제가 발생했습니다. 잠시후 다시 시도해주세요.',
        {
          variant: 'error'
        }
      );
    }

    if (result !== 'SUCCESS') {
      return enqueueSnackbar(
        '일시적인 오류로 서비스 접속에 실패했습니다. 새로고침 후 다시 시도해주세요. 새로고침 후에도 실패한 경우 채널톡으로 문의해주세요.',
        {
          variant: 'error'
        }
      );
    }

    onClose();

    return history.push(history.location.pathname);
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <CustomizedTypography
          variant="h4"
          color="textPrimary"
          fontWeight="bold"
        >
          {type === 'purchaseOrder' ? '발주하기' : '견적서 발행요청'}
        </CustomizedTypography>
        <IconButton className="close" onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <div className={classes.content}>
        {type === 'purchaseOrder' ? (
          <>
            <CustomizedTypography
              variant="h4"
              color="textPrimary"
              gutterBottoms={4}
            >
              파트너에게 견적서를 받지 않으셨습니다.
              <br />
              발주를 하기 위해서는 견적서를 요청하세요.
            </CustomizedTypography>

            <CustomizedTypography variant="body1" color="textSecondary">
              파트너에게 견적서 요청이 전송됩니다.
            </CustomizedTypography>
          </>
        ) : (
          <CustomizedTypography variant="h4" color="textPrimary">
            파트너에게 견적서 발행을 요청하시겠습니까?
          </CustomizedTypography>
        )}
      </div>
      <div className={classes.footer}>
        <CustomizedButton
          onClick={onClick}
          fullWidth
          variant="contained"
          color="primary"
          size="large"
        >
          견적서 요청하기
        </CustomizedButton>
      </div>
    </Dialog>
  );
};

export default RequestForIssueModal;

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 476,
    padding: 0,
    borderRadius: 16,
    '& h4': {
      wordBreak: 'break-all'
    }
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '28px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '28px 20px'
    },
    '& .close': {
      padding: 0,
      '& svg': {
        width: 24,
        height: 24
      }
    }
  },
  content: {
    padding: '0px 24px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '0px 16px 24px'
    }
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '16px'
    }
  }
}));
